import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create the context
const SubscriptionContext = createContext(null);

// Provider component
export function SubscriptionProvider({ children }) {
  const [subscription, setSubscription] = useState({
    user_id: null,
    is_premium: false,
    is_active: false,
    subscription_type: 'free',
    // Save limits
    max_saved_games: 3,
    saved_games_count: 0,
    can_save_more: true,
    saves_remaining: 3,
    // Play limits
    max_played_games: 3,
    played_games_count: 0,
    can_play_more: true,
    plays_remaining: 3,
    // Legacy
    games_remaining: 3,
    loading: true
  });

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubscription({
        ...response.data,
        loading: false
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
      setSubscription(prev => ({ ...prev, loading: false }));
      return null;
    }
  }, []);

  const checkCanSave = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/games/can-save`, { withCredentials: true });
      return response.data;
    } catch (error) {
      console.error('Failed to check save permission:', error);
      return { can_save: false, reason: 'Error checking permissions' };
    }
  }, []);

  const checkCanPlay = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/games/can-play`, { withCredentials: true });
      return response.data;
    } catch (error) {
      console.error('Failed to check play permission:', error);
      return { can_play: false, reason: 'Error checking permissions' };
    }
  }, []);

  const getSavedGames = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/games/saved`, { withCredentials: true });
      return response.data;
    } catch (error) {
      console.error('Failed to get saved games:', error);
      return { games: [], total: 0 };
    }
  }, []);

  const getGame = useCallback(async (gameId) => {
    try {
      const response = await axios.get(`${API}/games/${gameId}`, { withCredentials: true });
      return { success: true, game: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Game not found' };
    }
  }, []);

  const saveGame = useCallback(async (gameData) => {
    try {
      const response = await axios.post(`${API}/games/save`, gameData, { withCredentials: true });
      // Refresh subscription status after saving
      await fetchSubscriptionStatus();
      return { success: true, data: response.data };
    } catch (error) {
      const errorData = error.response?.data?.detail;
      if (errorData?.error === 'GAME_LIMIT_REACHED') {
        return { 
          success: false, 
          limitReached: true,
          ...errorData
        };
      }
      return { success: false, error: error.response?.data?.detail || 'Failed to save game' };
    }
  }, [fetchSubscriptionStatus]);

  const deleteGame = useCallback(async (gameId) => {
    try {
      await axios.delete(`${API}/games/${gameId}`, { withCredentials: true });
      // Refresh subscription status after deleting
      await fetchSubscriptionStatus();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to delete game' };
    }
  }, [fetchSubscriptionStatus]);

  const deleteAndReplace = useCallback(async (deleteGameId, newGameData) => {
    try {
      const response = await axios.post(`${API}/games/delete-and-replace`, {
        delete_game_id: deleteGameId,
        ...newGameData
      }, { withCredentials: true });
      // Refresh subscription status
      await fetchSubscriptionStatus();
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to replace game' };
    }
  }, [fetchSubscriptionStatus]);

  const trackPlay = useCallback(async (gameId, gameType) => {
    try {
      const response = await axios.post(`${API}/games/track-play`, {
        game_id: gameId,
        game_type: gameType
      }, { withCredentials: true });
      // Refresh subscription status
      await fetchSubscriptionStatus();
      return { success: true, data: response.data };
    } catch (error) {
      const errorData = error.response?.data?.detail;
      if (errorData?.error === 'PLAY_LIMIT_REACHED') {
        return {
          success: false,
          limitReached: true,
          ...errorData
        };
      }
      return { success: false, error: error.response?.data?.detail || 'Failed to track play' };
    }
  }, [fetchSubscriptionStatus]);

  // Fetch subscription status on mount
  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  const value = {
    subscription,
    fetchSubscriptionStatus,
    checkCanSave,
    checkCanPlay,
    getSavedGames,
    getGame,
    saveGame,
    deleteGame,
    deleteAndReplace,
    trackPlay,
    // Convenience getters
    isPremium: subscription.is_premium,
    userId: subscription.user_id,
    // Save limits
    canSaveMore: subscription.can_save_more,
    savedGamesCount: subscription.saved_games_count,
    maxSavedGames: subscription.max_saved_games,
    savesRemaining: subscription.saves_remaining,
    // Play limits
    canPlayMore: subscription.can_play_more,
    playedGamesCount: subscription.played_games_count,
    maxPlayedGames: subscription.max_played_games,
    playsRemaining: subscription.plays_remaining,
    // Legacy
    gamesRemaining: subscription.games_remaining
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

// Custom hook to use subscription context
export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

export default SubscriptionContext;
